import { Button, Grid, Typography } from '@mui/material';
import { Auth } from 'aws-amplify';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ColorType } from '../../assets/colors/colors.g';
import { useAuth } from '../../hooks/useAuth';
import { useDomain } from '../../hooks/useDomain';
import { useLogger } from '../../services/logging/logger-context';
import { useLazyHasHomeChargersQuery } from '../../services/locations.api';
import { useFetchActiveSessionsQuery } from '../../services/sessions.api';
import { ROUTES } from '../../types/routes.enum';
import { showSnackBar } from '../../util/toast.utility';
import { LoadingDots } from '../ui-components/loading-dots/loading-dots.component';
import { BackButtonHeader } from '../ui-components/back-button-header.component';
import { CancelButton } from '../ui-components/button/cancelButton';
import { ViewWrapper } from '../ui-components/view-wrapper.component';
import { useStyles } from '../logout/styles';
import { DeleteAccountBlockDialog } from './delete-account-block-dialog.component';

const DeleteAccount = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const chargerName = new URLSearchParams(location.search).get('charger');
  const { i18n } = useTranslation();
  const auth = useAuth();
  const { logger } = useLogger();
  const { domain } = useDomain();
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteBlockMessage, setDeleteBlockMessage] = useState<string | null>(null);
  const [triggerHasHomeChargers] = useLazyHasHomeChargersQuery();
  const { data: activeSessions } = useFetchActiveSessionsQuery(undefined, {
    skip: !auth.isLoggedIn,
  });
  const hasActiveSession = Boolean(activeSessions?.length);

  const backAction = () => {
    const route = chargerName
      ? `/app/${domain}/${ROUTES.ACCOUNT}?charger=${chargerName}`
      : `/app/${domain}/${ROUTES.ACCOUNT}`;
    navigate(route);
  };

  const deleteAccount = async () => {
    if (isDeletingAccount) {
      return;
    }

    if (hasActiveSession) {
      setDeleteBlockMessage(i18n.t('delete_account_session_active'));
      return;
    }

    setIsDeletingAccount(true);
    try {
      const hasHomeChargers = await triggerHasHomeChargers().unwrap();
      if (hasHomeChargers) {
        setDeleteBlockMessage(i18n.t('delete_account_home_chargers'));
        setIsDeletingAccount(false);
        return;
      }
    } catch (error: unknown) {
      const checkError = error instanceof Error ? error : new Error('Unknown error');
      logger.error('Failed to check home chargers before account deletion:', checkError);
      showSnackBar(i18n.t('delete_account_error'));
      setIsDeletingAccount(false);
      return;
    }

    try {
      await Auth.deleteUser();
      logger.info('User account deleted');
      const redirectRoute = chargerName
        ? `/app/${domain}/${ROUTES.LOGIN}?charger=${chargerName}`
        : `/app/${domain}/${ROUTES.LOGIN}`;
      auth.signOut(redirectRoute);
    } catch (error: unknown) {
      const deleteError = error instanceof Error ? error : new Error('Unknown error');
      logger.error('Failed to delete user account:', deleteError);
      showSnackBar(i18n.t('delete_account_error'));
      setIsDeletingAccount(false);
    }
  };

  return (
    <ViewWrapper>
      <Grid className={classes.logoutWrapper}>
        <BackButtonHeader title={`${i18n.t('delete_account')}?`} onBackArrowClicked={backAction} />
        <Grid className={classes.container}>
          <Typography className={classes.title} variant='h5' component='h1'>
            {i18n.t('confirm_delete_account')}
          </Typography>
          <Typography className={classes.subTitle} variant='subtitle1'>
            {i18n.t('delete_account_confirm_description')}
          </Typography>
        </Grid>
        <Grid container className={classes.root} spacing={1}>
          <Grid item xs={6}>
            <CancelButton type='button' onClick={backAction} disabled={isDeletingAccount}>
              {i18n.t('cancel')}
            </CancelButton>
          </Grid>
          <Grid item xs={6}>
            <Button
              type='button'
              variant='outlined'
              className={classes.buttonItem}
              sx={{
                height: '48px',
                backgroundColor: ColorType.WHITE,
                borderColor: ColorType.NEGATIVE,
                color: ColorType.NEGATIVE,
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '16px',
                ':hover': {
                  backgroundColor: ColorType.NEGATIVE0,
                  borderColor: ColorType.NEGATIVE,
                },
                '&.Mui-disabled': {
                  backgroundColor: ColorType.WHITE,
                  borderColor: ColorType.GREY3,
                  color: ColorType.GREY4,
                },
              }}
              onClick={deleteAccount}
              disabled={isDeletingAccount}
            >
              {isDeletingAccount ? <LoadingDots /> : i18n.t('delete_account')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <DeleteAccountBlockDialog
        open={Boolean(deleteBlockMessage)}
        message={deleteBlockMessage ?? ''}
        onClose={() => setDeleteBlockMessage(null)}
      />
    </ViewWrapper>
  );
};

export default DeleteAccount;
